import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';

import type {
  BreakdownField,
  FieldErrors,
} from '@app/domain/models/errors/backend-error.model';
import { parseBackendErrorBody } from '@app/domain/models/errors/backend-error.parser';
import { lockMinutes, readTotpLock } from '@app/domain/models/errors/totp-lock';
import {
  breakdownLines,
  localeFor,
  parseBreakdown,
} from '@app/domain/models/errors/insufficient-funds';

/** Cabecera del contrato; el backend la expone por CORS. */
const TRACE_ID_HEADER = 'X-Trace-Id';

/**
 * Sufijo i18n de cada campo del desglose. El backend usa `snake_case` y las
 * claves de Transloco son `camelCase`; este mapa es el único punto que conoce
 * ambos vocabularios.
 */
const BREAKDOWN_KEY: Readonly<Record<BreakdownField, string>> = {
  saldo: 'saldo',
  en_transito: 'enTransito',
  disponible: 'disponible',
  monto_envio: 'montoEnvio',
  comision_con_iva: 'comisionConIva',
  requerido: 'requerido',
  faltante: 'faltante',
};

export interface ResolvedError {
  /** Siempre poblado. SIEMPRE traducido: nunca texto del backend. */
  readonly text: string;
  /**
   * Siempre poblado: lo que dijo el cuerpo y, solo si no dijo nada, lo que
   * delata el status. Describe el fallo; no autoriza a reintentar solo (un alta
   * sigue necesitando su `Idempotency-Key`).
   */
  readonly retryable: boolean;
  /** Se omite cuando no se conoce (R9: sin trace, sin UI de trace). */
  readonly traceId?: string;
  readonly upstreamTraceId?: string;
  /** Líneas traducidas del desglose de `INSUFFICIENT_FUNDS`, si las hay. */
  readonly lines?: string[];
  readonly fieldErrors?: FieldErrors;
}

export interface ResolveOptions {
  /**
   * Clave que sustituye al mapeo por status en el peldaño 2. Es como los parches
   * por URL que D2 manda conservar entran a la escalera: DEBAJO del `error_code`.
   */
  readonly statusFallbackKey?: string;
}

/**
 * Escalera de fallback del contrato de errores (ola 0), en tres peldaños:
 *
 *   1. `error_code` conocido → `errors.codes.<CODE>`
 *   2. mensaje por status HTTP (exactamente el `if/else` que el interceptor
 *      aplicaba hoy) o la clave que pase quien llama
 *   3. `errors.http.unexpected`
 *
 * `CLAUDE.md` §Security: el texto del backend se lee SOLO para clasificar y no
 * sale nunca hacia la UI. Lo que se muestra son claves que el front posee.
 */
@Injectable({ providedIn: 'root' })
export class ErrorCopyService {
  private readonly transloco = inject(TranslocoService);

  resolve(err: HttpErrorResponse, options?: ResolveOptions): ResolvedError {
    const headerTraceId = err.headers?.get(TRACE_ID_HEADER) ?? null;
    const parsed = parseBackendErrorBody(err.error, headerTraceId);

    // R41 (contrato §5, «⚠️ CORRECCIÓN 2026-09-08»): el `retryable` del cuerpo
    // GANA sobre el status. El respaldo por status vale SOLO cuando el cuerpo no
    // trajo la llave — o sea `null`, no `false`. Con el `||` anterior, los 5xx
    // que son `retryable: false` a propósito volvían a salir reintentables y la
    // UI ofrecía un «Reintentar» que no puede funcionar.
    //
    // Son al menos CUATRO códigos: `UPSTREAM_AUTH_FAILED`, `UPSTREAM_TLS_FAILED`
    // y `UPSTREAM_NOT_CONFIGURED` (configuración nuestra, no una caída) más
    // `UPSTREAM_REJECTED` (el micro rechazó de verdad). El contrato dice «tres»
    // y SUBCUENTA: su propio fixture trae `UPSTREAM_REJECTED` como 502 + `false`
    // en dos cuerpos. Esta línea no enumera códigos a propósito —es la regla, no
    // una lista— y quien manda es la tabla de `error-copy.service.spec.ts`, que
    // asserta los siete cuerpos reales.
    const retryable =
      parsed.retryable ?? (err.status === 0 || err.status >= 500);

    const base = {
      retryable,
      ...(parsed.traceId ? { traceId: parsed.traceId } : {}),
      ...(parsed.upstreamTraceId ? { upstreamTraceId: parsed.upstreamTraceId } : {}),
      ...(parsed.fieldErrors ? { fieldErrors: parsed.fieldErrors } : {}),
    };

    // El bloqueo del 2FA dice CUÁNTO esperar cuando banca lo sabe (minutos
    // redondeados hacia arriba). Sin segundos cae al peldaño 1 de siempre.
    const lock = readTotpLock(err.status, err.error, err.headers?.get('Retry-After'));
    if (lock?.retryAfterSeconds) {
      return {
        ...base,
        text: this.transloco.translate<string>('errors.totpLocked.minutes', {
          minutes: lockMinutes(lock.retryAfterSeconds),
        }),
      };
    }

    // PELDAÑO 1 — el código conocido gana.
    if (parsed.code) {
      const key = `errors.codes.${parsed.code}`;
      const text = this.transloco.translate<string>(key);
      // GUARDA CRÍTICA: `DefaultMissingHandler` devuelve la clave tal cual. Sin
      // esta comparación, un código que el backend agregue mañana pintaría
      // literalmente `errors.codes.LO_QUE_SEA` en la pantalla del usuario.
      if (text !== key) {
        const lines = this.buildBreakdownLines(parsed.code, parsed.breakdown);
        return { ...base, text, ...(lines ? { lines } : {}) };
      }
    }

    // PELDAÑOS 2 y 3.
    const fallbackKey = options?.statusFallbackKey ?? keyForStatus(err.status);
    return { ...base, text: this.transloco.translate<string>(fallbackKey) };
  }

  /**
   * El copy del bloqueo del 2FA por intentos (429 `TOTP_LOCKED`), o `null` si
   * el error no es ese. Para las pantallas que CIERRAN la ventana del código y
   * pintan su propio mensaje de resultado: sin esto dirían "no se pudo" sin
   * decir por qué ni cuánto esperar. Mismo texto que la escalera.
   */
  totpLockText(err: unknown): string | null {
    if (!(err instanceof HttpErrorResponse)) return null;
    const lock = readTotpLock(err.status, err.error, err.headers?.get('Retry-After'));
    if (!lock) return null;
    return lock.retryAfterSeconds
      ? this.transloco.translate<string>('errors.totpLocked.minutes', {
          minutes: lockMinutes(lock.retryAfterSeconds),
        })
      : this.transloco.translate<string>('errors.codes.TOTP_LOCKED');
  }

  /**
   * Desglose de `INSUFFICIENT_FUNDS` traducido (R29). Sin desglose devuelve
   * `undefined` y quien llama cae a su comportamiento de hoy (R33).
   */
  private buildBreakdownLines(code: string, rawBreakdown: unknown): string[] | undefined {
    if (code !== 'INSUFFICIENT_FUNDS') return undefined;
    const locale = localeFor(this.transloco.getActiveLang());
    const lines = breakdownLines(parseBreakdown(rawBreakdown), locale);
    if (lines.length === 0) return undefined;
    return lines.map(line =>
      this.transloco.translate<string>(`errors.insufficientFunds.${BREAKDOWN_KEY[line.field]}`, {
        amount: line.amount,
      }),
    );
  }
}

/**
 * Peldaño 2: reproduce EXACTAMENTE el `if/else` que `handleGlobalErrors` aplicaba
 * antes de esta ola, menos el parche por URL, que entra por `statusFallbackKey`.
 */
export function keyForStatus(status: number): string {
  if (status === 0) return 'errors.http.noConnection';
  if (status >= 500) return 'errors.http.serverError';
  if (status === 400) return 'errors.http.badRequest';
  if (status === 401) return 'errors.http.unauthorized';
  if (status === 403) return 'errors.http.forbidden';
  return 'errors.http.unexpected';
}
